import { useState, useEffect } from 'react';
import { Popconfirm, message } from 'antd';
import { LuX } from 'react-icons/lu';
import { motion } from 'motion/react';
import { darkenHex, lightenHex, accentRgba } from './utils.js';

function SkillsModal({ onClose, skills, onChangeSkills, accentColor }) {
	const [input, setInput] = useState('');

	useEffect(() => {
		document.body.style.overflow = 'hidden';
		const handleEsc = (e) => {
			if (e.key === 'Escape') onClose();
		};
		document.addEventListener('keydown', handleEsc);
		return () => {
			document.body.style.overflow = '';
			document.removeEventListener('keydown', handleEsc);
		};
	}, [onClose]);

	const handleAdd = () => {
		const value = input.trim().toLowerCase();
		if (!value) {
			message.error('Skill can not be empty');
			return;
		}
		if (skills.includes(value)) {
			message.error('Skill already exists');
			return;
		}
		onChangeSkills([...skills, value]);
		setInput('');
	};

	const handleRemove = (index) => {
		onChangeSkills(skills.filter((_, i) => i !== index));
	};

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center p-4"
			onClick={onClose}
		>
			<style>{`.career-hub-skill-wrapper:hover .career-hub-skill-delete { opacity: 1 !important; transform: scale(1) !important; }`}</style>

			<motion.div
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				exit={{ opacity: 0 }}
				transition={{ duration: 0.3 }}
				className="fixed inset-0"
				style={{
					background: 'rgba(15,23,42,0.5)',
					backdropFilter: 'blur(4px)',
				}}
			/>

			<motion.div
				initial={{ opacity: 0, scale: 0.95 }}
				animate={{ opacity: 1, scale: 1 }}
				exit={{ opacity: 0, scale: 0.95 }}
				transition={{ duration: 0.3, ease: 'easeOut' }}
				className="relative flex w-full flex-col overflow-hidden rounded-2xl bg-white"
				style={{
					maxWidth: 560,
					maxHeight: '90vh',
					boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
				}}
				onClick={(e) => e.stopPropagation()}
			>
				<div
					className="flex items-center justify-between p-6"
					style={{ paddingBottom: 0 }}
				>
					<h2
						style={{
							color: '#1e293b',
							fontSize: 18,
							fontWeight: 600,
							margin: 0,
						}}
					>
						Manage Skills
					</h2>
					<button
						type="button"
						onClick={onClose}
						style={{
							width: 32,
							height: 32,
							borderRadius: 12,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							padding: 0,
							lineHeight: 0,
							color: '#475569',
							background: 'none',
							border: 'none',
							cursor: 'pointer',
							transition: 'all 0.2s',
						}}
						onMouseOver={(e) => {
							e.currentTarget.style.color = '#475569';
							e.currentTarget.style.background = '#f1f5f9';
						}}
						onMouseOut={(e) => {
							e.currentTarget.style.color = '#475569';
							e.currentTarget.style.background = 'none';
						}}
					>
						<LuX size={14} />
					</button>
				</div>

				<div
					className="overflow-y-auto p-6"
					style={{
						scrollbarWidth: 'thin',
						scrollbarColor: '#cbd5e1 transparent',
					}}
				>
					<div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
						<div>
							<div
								className="mb-1.5 flex items-center justify-between"
								style={{ color: '#334155', fontSize: 14, fontWeight: 600 }}
							>
								<span>Skills</span>
								{skills.length > 0 && (
									<Popconfirm
										title="Remove all skills?"
										okText="Remove All"
										cancelText="Cancel"
										onConfirm={() => onChangeSkills([])}
									>
										<button
											type="button"
											style={{
												background: 'none',
												border: 'none',
												color: '#dc2626',
												fontSize: 12,
												fontWeight: 600,
												cursor: 'pointer',
												padding: 0,
											}}
										>
											Remove All
										</button>
									</Popconfirm>
								)}
							</div>

							{skills.length > 0 ? (
								<div className="mb-2 flex flex-wrap gap-1.5">
									{skills.map((skill, index) => {
										const display = skill
											.split(' ')
											.map((s) => s.charAt(0).toUpperCase() + s.slice(1))
											.join(' ');
										return (
											<div
												key={`${skill}-${index}`}
												className="career-hub-skill-wrapper"
												style={{ position: 'relative', display: 'inline-flex' }}
											>
												<span
													style={{
														paddingLeft: 10,
														paddingRight: 10,
														paddingTop: 6,
														paddingBottom: 6,
														borderRadius: 8,
														border: '1px solid',
														borderColor: accentRgba(accentColor, 0.3),
														background: accentRgba(accentColor, 0.1),
														color: accentColor,
														fontSize: 11,
														fontWeight: 500,
														lineHeight: 1,
														display: 'inline-flex',
														alignItems: 'center',
													}}
												>
													{display}
												</span>
												<button
													type="button"
													className="career-hub-skill-delete"
													onClick={() => handleRemove(index)}
													style={{
														position: 'absolute',
														top: -5,
														right: -5,
														width: 16,
														height: 16,
														borderRadius: '50%',
														background: '#ef4444',
														color: '#fff',
														border: '2px solid #fff',
														cursor: 'pointer',
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'center',
														padding: 0,
														lineHeight: 1,
														fontSize: 10,
														opacity: 0,
														transform: 'scale(0.5)',
														transition: 'opacity 0.15s, transform 0.15s',
														zIndex: 1,
													}}
												>
													<LuX size={8} />
												</button>
											</div>
										);
									})}
								</div>
							) : (
								<p
									style={{
										color: '#475569',
										fontSize: 13,
										margin: '4px 0 8px',
									}}
								>
									No skills yet
								</p>
							)}

							<div className="flex gap-2">
								<input
									type="text"
									value={input}
									onChange={(e) => setInput(e.target.value)}
									onKeyDown={(e) => {
										if (e.key === 'Enter') {
											e.preventDefault();
											handleAdd();
										}
									}}
									placeholder="Add a skill..."
									className="rounded-lg text-xs"
									style={{
										flex: 1,
										padding: '6px 12px',
										background: '#f8fafc',
										border: '1px solid #e2e8f0',
										outline: 'none',
										color: '#334155',
										transition: 'all 0.2s',
									}}
									onFocus={(e) => {
										e.target.style.borderColor = lightenHex(accentColor);
										e.target.style.boxShadow = `0 0 0 3px ${accentRgba(accentColor, 0.1)}`;
									}}
									onBlur={(e) => {
										e.target.style.borderColor = '#e2e8f0';
										e.target.style.boxShadow = 'none';
									}}
								/>
								<button
									type="button"
									onClick={handleAdd}
									style={{
										padding: '6px 12px',
										fontSize: 12,
										fontWeight: 600,
										color: accentColor,
										background: 'none',
										border: 'none',
										cursor: 'pointer',
										transition: 'color 0.2s',
									}}
									onMouseOver={(e) => {
										e.currentTarget.style.color = darkenHex(accentColor);
									}}
									onMouseOut={(e) => {
										e.currentTarget.style.color = accentColor;
									}}
								>
									Add
								</button>
							</div>
						</div>

						<p
							style={{
								fontSize: 12,
								color: '#475569',
								margin: 0,
								fontStyle: 'italic',
							}}
						>
							Changes are saved when you click "Update Profile".
						</p>

						<div className="flex gap-3 pt-2">
							<button
								type="button"
								onClick={onClose}
								style={{
									flex: 1,
									paddingTop: 10,
									paddingBottom: 10,
									borderRadius: '0.75rem',
									fontSize: 14,
									fontWeight: 600,
									background: '#f1f5f9',
									color: '#475569',
									border: 'none',
									cursor: 'pointer',
									transition: 'background 0.2s',
								}}
								onMouseOver={(e) => {
									e.currentTarget.style.background = '#e2e8f0';
								}}
								onMouseOut={(e) => {
									e.currentTarget.style.background = '#f1f5f9';
								}}
							>
								Done
							</button>
						</div>
					</div>
				</div>
			</motion.div>
		</div>
	);
}

export default SkillsModal;
